import { destination } from "@turf/turf";
import { useCallback, useContext, useState } from "react";
import { useMap } from "react-map-gl/maplibre";

import { MapResult } from "@/api";
import { useMapStyle } from "@/hooks/use-map-style";
import { ProjectContext } from "@/stores/project-provider";
import { displayNumber } from "@/utils/get-display-number";
import { getFlippedPolygonLayer } from "@/utils/get-flipped-polygon-layer";

import { useGeoTIFFLayer } from "./use-geotiff-layer";

// Distance candidates (meters); pick one that yields good visual width
const DISTANCE_CANDIDATES = [5, 10, 20, 50, 100, 200, 500, 1000, 2000, 5000];

export const useMapLayers = (mapResult: MapResult | undefined) => {
  const { project } = useContext(ProjectContext);
  const bitmapLayer = useGeoTIFFLayer(mapResult);
  return [
    bitmapLayer,
    ...(project.area?.coordinates ? [getFlippedPolygonLayer(project.area.coordinates[0])] : []),
  ];
};

const getScaleText = (meters: number | undefined) => {
  if (meters === undefined) return null;

  if (meters >= 1000) {
    return `${displayNumber(meters / 1000)} km`;
  }

  return `${meters} m`;
};

export const useReportMap = (mapId: string, mapResult: MapResult | undefined) => {
  const layers = useMapLayers(mapResult);

  const map = useMap()[mapId];
  const mapStyle = useMapStyle();

  const [scale, setScale] = useState<{ px: number; meters: number } | null>(null);

  // Compute pixel length representing best distance choice at current center/zoom
  const computeScale = useCallback(() => {
    if (!map?.getCenter || !map.project) return;
    const center = map.getCenter();
    if (!center) return;

    // Helper to get pixel width for a given distance (meters) eastwards
    const measureDistance = (meters: number) => {
      const from: [number, number] = [center.lng, center.lat];
      const km = meters / 1000;
      const to = destination(from, km, 90, { units: "kilometers" }).geometry.coordinates.splice(
        0,
        2
      ) as [number, number];
      const p1 = map.project(from);
      const p2 = map.project(to);
      return Math.abs(p2.x - p1.x);
    };

    // Choose candidate that yields width in nice visual range
    const MIN_PX = 60;
    const MAX_PX = 160;
    let bestChoice: { meters: number; px: number } | null = null;

    for (const meters of DISTANCE_CANDIDATES) {
      const px = measureDistance(meters);

      // Prefer distances that fall within our target pixel range
      if (px >= MIN_PX && px <= MAX_PX) {
        bestChoice = { meters, px };
        break;
      }

      // Fallback: track the one closest to MIN_PX if none fit perfectly
      if (!bestChoice || (px > bestChoice.px && bestChoice.px < MIN_PX)) {
        bestChoice = { meters, px };
      }
    }

    if (bestChoice && Number.isFinite(bestChoice.px) && bestChoice.px > 0) {
      setScale(bestChoice);
    }
  }, [map]);

  return {
    layers,
    mapStyle,
    computeScale,
    scaleWidthPx: scale?.px ?? null,
    scaleMeters: scale?.meters ?? null,
    scaleText: getScaleText(scale?.meters),
  };
};
